import React from 'react';

import { ProductHero } from '@root/components/www/shared/product_hero';
import { ProductOverview } from '@root/components/www/shared/product_overview';
import { ProductTeam } from '@root/components/www/shared/product_team';

import { Footer } from '@shared/content/footer';
import { IconValueProps } from '@shared/page_builder/components/icon_value_props';
import { ServiceSelection } from '@shared/service_selector/constants';
import { ZebraContainer, ZebraPattern } from '@shared/zebra_container';

import calendar from '@images/illustrations/calendar.svg';
import clutterTruck from '@images/illustrations/mover_with_truck.svg';
import onlineQuote from '@images/illustrations/online_quote_check.svg';
import movingMainImage from '@images_responsive/www/services/clutter_customer_with_dog_and_truck.jpg';
import movingHero from '@images_responsive/www/services/long_distance_hero.jpg';
import movingSecondaryImage from '@images_responsive/www/services/movers_loading_van.jpg';

import { Header } from '../../../shared/header/header';
import { FAQCategories } from '../faq_categories';

const valueProps = [
  {
    title: 'Professional full-service moving',
    description:
      'A dedicated professional team will help you pack when you need it and securely handle and transport your items to your destination.',
    illustration: clutterTruck,
  },
  {
    title: 'Flexible delivery',
    description:
      'Big moves come hand-in-hand with big changes. We offer full flexibility whether you are ready to schedule your delivery day or need a little extra time uncluttered.',
    illustration: calendar,
  },
  {
    title: 'One free month of storage',
    description:
      'Take advantage of our free month of storage. Drive the scenic route and take a break. We’ll hold onto your items while you make your way to your next big chapter.',
    illustration: onlineQuote,
  },
];

const teamValueProps = [
  {
    headline: 'Professionally trained',
    description:
      'We’ve invested in building a full-time team of professional movers who are thoroughly vetted to be the best of the best.',
  },
  {
    headline: 'A full-time team',
    description:
      'All team members are full-time employees with benefits like health insurance and paid time off.',
  },
  {
    headline: 'Your stuff is in good hands',
    description:
      'We train our team to sweat the details and to handle your items with the utmost care. The things you love are safe with us.',
  },
];

const faqContent = [
  {
    question: 'What counts as a long distance move?',
    answer: (
      <>
        Long distance moves are generally over 100 miles between your pickup and
        destination addresses or the mileage limitations set by your state’s
        regulations.
      </>
    ),
  },
  {
    question: 'How does pricing work for long distance moves?',
    answer: (
      <>
        Your quote will include a labor rate for packing and unpacking along
        with a transport fee based on the estimated size, complexity, and
        distance of your upcoming move.
      </>
    ),
  },
  {
    question: 'Can I choose an exact pickup and delivery date?',
    answer: (
      <>
        Yes! Unlike other moving companies that only provide a wide delivery
        window, with Clutter, you get to choose your guaranteed pickup and
        delivery dates for no extra fee.
      </>
    ),
  },
  {
    question: 'When should I book my move?',
    answer: (
      <>
        We can schedule moves with as little as three day’s notice, subject to
        availability. Availability books up fast, so we recommend booking as
        early as possible to lock in your date.
      </>
    ),
  },
  {
    question: 'Do I have to be with the movers during the move?',
    answer: (
      <>
        Yes, you or a designated point of contact must be present when our team
        of movers pack and load the truck, as well as when movers unload (and
        unpack) at your destination. We recommend someone be present to sign-off
        on starting and stopping the clock during both the pickup and delivery.
      </>
    ),
  },
  {
    question: 'How long does it generally take for my items to arrive?',
    answer: (
      <>
        You can schedule your guaranteed delivery date as early as 5 days for
        moves under 1,000 miles and as early as 10 days for moves across the
        country, you will be notified if your items arrive at our warehouse
        sooner.
      </>
    ),
  },
];

export const LongDistanceMovingServicePage = () => {
  return (
    <>
      <Header service={ServiceSelection.Moving} />
      <ZebraContainer zebra={ZebraPattern.LIGHT_THEN_DARK}>
        <ProductHero
          title="Long distance moving"
          subtitle="Moving far away? Clutter will help you go the distance"
          image={movingHero}
          service={ServiceSelection.Moving}
        />
        <ProductOverview
          title="Move long-distance and store for free"
          subtitle="Get an instant quote and secure your move in minutes."
          mainImage={movingMainImage}
          secondaryImage={movingSecondaryImage}
          service={ServiceSelection.Moving}
          valueProps={[
            'Dedicated professional moving team',
            'Guaranteed delivery date & time',
            'Basic and full-service flat rates',
            'First month of storage free',
          ]}
        />
        <IconValueProps
          title="Effortless, flexible long distance moves"
          valueProps={valueProps}
          cta={{ service: ServiceSelection.Moving }}
        />
        <ProductTeam title="Meet your moving team" content={teamValueProps} />
        <FAQCategories
          headline={<>Common questions about long distance moving.</>}
          categories={[{ questions: faqContent }]}
        />
      </ZebraContainer>
      <Footer />
    </>
  );
};
